import React from 'react';
import { Bar, ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';

const TeamLeaderboard = ({ data = [], loading = false, period = 'thisMonth' }) => {
  // Bepaal het doel per medewerker op basis van de periode
  const getEmployeeTarget = () => {
    if (period === 'today' || period === 'yesterday') {
      return 1000; // €1000 per dag per medewerker
    } else if (period === 'thisWeek' || period === 'lastWeek') {
      return 5000; // €5000 per week per medewerker (5 dagen × €1000)
    } else {
      return 21650; // €21650 per maand per medewerker (4.33 weken × €5000)
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  if (loading) {
    return (
      <div className="card-blue">
        <div className="card-header">
          <div style={{ height: '24px', width: '150px', background: 'var(--color-neutral-200)', borderRadius: 'var(--radius-md)' }} className="animate-pulse" />
        </div>
        <div className="card-body">
          <div style={{ height: '256px', background: 'var(--color-neutral-100)', borderRadius: 'var(--radius-md)' }} className="animate-pulse" />
        </div>
      </div>
    );
  }

  const employeeTarget = getEmployeeTarget();

  const chartData = data.map(employee => ({
    name: employee.name,
    revenue: employee.revenue,
    revenueFormatted: formatCurrency(employee.revenue),
    jobs: employee.jobs || 0,
    target: employeeTarget,
    targetFormatted: formatCurrency(employeeTarget),
    color: employee.color
  }));

  // Custom tooltip component with design system styling
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{
          background: 'white',
          padding: 'var(--space-3) var(--space-4)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)',
          border: '1px solid var(--color-neutral-200)'
        }}>
          <p style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 'var(--font-semibold)',
            color: 'var(--color-neutral-900)',
            marginBottom: 'var(--space-2)'
          }}>{label}</p>
          <p style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--color-blue-600)',
            fontWeight: 'var(--font-medium)'
          }}>Omzet: {data.revenueFormatted}</p>
          <p style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--color-neutral-600)'
          }}>Klussen: {data.jobs}</p>
          <p style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--color-neutral-500)'
          }}>Doel: {data.targetFormatted}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="card-blue">
      <div className="card-header">
        <h3 className="card-title">Team Leaderboard</h3>
      </div>
      <div className="card-body">
        <div style={{ height: '256px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 20, right: 50, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-neutral-200)" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 12, fill: 'var(--color-neutral-600)' }}
                angle={-45}
                textAnchor="end"
                height={80}
                axisLine={{ stroke: 'var(--color-neutral-300)' }}
              />
              <YAxis
                yAxisId="revenue"
                domain={[0, employeeTarget]}
                tickFormatter={formatCurrency}
                tick={{ fontSize: 12, fill: 'var(--color-neutral-600)' }}
                axisLine={{ stroke: 'var(--color-neutral-300)' }}
              />
              <YAxis
                yAxisId="jobs"
                orientation="right"
                domain={[0, 'dataMax + 2']}
                tick={{ fontSize: 12, fill: 'var(--color-neutral-600)' }}
                axisLine={{ stroke: 'var(--color-neutral-300)' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar yAxisId="revenue" dataKey="revenue" name="Omzet" radius={[4, 4, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
              <Bar yAxisId="jobs" dataKey="jobs" name="Klussen" fill="var(--color-neutral-400)" opacity={0.7} radius={[4, 4, 0, 0]} />
              {period !== 'thisYear' && (
                <ReferenceLine
                  yAxisId="revenue"
                  y={employeeTarget}
                  stroke="var(--color-error)"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  label={{ value: "Doel", position: "topRight", fill: 'var(--color-error)', fontSize: 12 }}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default TeamLeaderboard;
